/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import Table from "components/Tables/Table/Table.js";

import { makeStyles } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";

import { TFunction, useTranslation } from "react-i18next";

import styles from "assets/jss/material-dashboard-pro-react/views/userProfileStyles.js";
import Badge from "components/UI/Badge/Badge";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";
import { ExtractTypeEnum, WorkItemResultEnum } from "utils/@types";
import { StyledParagraph } from "./AnalysisModal.styles";
import Box from "@mui/material/Box";
import { useClientConfigTranslation } from "services/blob/get-client-config-translation";
import { useLanguageSettings } from "store/user-settings/user-settings-store";
import { AnalysisModalSkeleton } from "./AnalysisModalSkeleton";

const useStyles = makeStyles(styles as any);

const tableHeaders = ["field", "expected", "extracted", "status"];

const renderAnalysisStatus = (status: any, t: any) => {
  const renderedStatus = (status: any, color: any) => (
    <Badge color={color}>
      {t(`modal-table-status-${status}`, { ns: "status" })}
    </Badge>
  );

  switch (status) {
    case WorkItemResultEnum.HOMOLOGADO:
      return renderedStatus("valid", "success");
    case WorkItemResultEnum.HOMOLOGATED:
      return renderedStatus("valid", "success");
    case WorkItemResultEnum.MANUALLY_HOMOLOGATED:
      return renderedStatus("valid", "success");
    case WorkItemResultEnum.Approved:
      return renderedStatus("valid", "success");
    case WorkItemResultEnum.NAO_HOMOLOGADO:
      return renderedStatus("invalid", "danger");
    case WorkItemResultEnum.Not_Approved:
      return renderedStatus("invalid", "danger");
    default:
      return renderedStatus("error", "warning");
  }
};

const renderAnalysisExtracted = (
  extract: string,
  t: TFunction<"analysis-modal", undefined>,
  fieldName: string
) => {
  const extractTranslations: Record<string, string> = {
    [ExtractTypeEnum.INVALIDO]: "modal-table-extract-invalid-doc",
    [ExtractTypeEnum.CAMPO_NAO_ENCONTRADO]: "modal-table-extract-invalid-field",
    [ExtractTypeEnum.DOCUMENTO_INVALIDO]:
      "modal-table-extract-invalid-document",
    [ExtractTypeEnum.TIPO_DOCUMENTO]: "modal-table-extract-document-type",
    [ExtractTypeEnum.RULE_FIELD_NOT_FOUND]:
      "modal-table-extract-field-not-found",
    [ExtractTypeEnum.RULEFIELD_NOT_FOUND]:
      "modal-table-extract-field-not-found",
    [ExtractTypeEnum.PREENCHIDO]: "modal-table-extract-filled",
    [ExtractTypeEnum.NAO_PREENCHIDO]: "modal-table-extract-field-not-filled",
    [ExtractTypeEnum.DOCUMENTO_PREENCHIDO]:
      "modal-table-extract-value-completed-document",
    [ExtractTypeEnum.INVALID_DOCUMENT]: "modal-table-extract-invalid-doc",
    [ExtractTypeEnum.ATTACHMENTS_COUNT]:
      "modal-table-extract-attachments-count",
    [ExtractTypeEnum.INVALID_DATE]: "modal-table-extract-invalid-date",
    [ExtractTypeEnum.FIELDGLASS_UNAVAILABLE]:
      "modal-table-fieldglass-unavailable",
    [ExtractTypeEnum.DOCUMENT_USED_AGAIN]: "modal-table-document-used-again",
    [ExtractTypeEnum.FIELDGLASS_UNAVAILABLE]:
      "modal-table-fieldglass-unavailable",
    [ExtractTypeEnum.DOCUMENT_USED_AGAIN]: "modal-table-document-used-again",
    [ExtractTypeEnum.WORKERSNAMES]: "modal-table-workers-names",
  };

  if (extract in extractTranslations) {
    return t(extractTranslations[extract]);
  }

  if (fieldName === "contractsNumber" && extract) {
    try {
      return Array.isArray(JSON.parse(extract))
        ? JSON.parse(extract).join(" - ")
        : extract.split("/").join(" - ");
    } catch (_) {
      return extract.split("/").join(" - ");
    }
  }

  return extract;
};

interface AnalysisModalProps {
  analysis: any;
  modal: boolean;
  handleClose: () => void;
}

export default function AnalysisModal({
  analysis,
  modal,
  handleClose,
}: AnalysisModalProps) {
  const classes = useStyles();
  const { t } = useTranslation("analysis-modal");
  const { language } = useLanguageSettings();
  const { data: translations, isLoading } = useClientConfigTranslation(
    language === "it" ? "ita" : language
  );

  const analyzedContent =
    analysis?.map((analysis: any) => {
      const translationKey =
        translations?.[analysis?.fieldName as keyof typeof translations];
      return [
        translationKey || "Key not found",
        renderAnalysisExtracted(
          analysis.fieldCompared ?? ExtractTypeEnum.NAO_PREENCHIDO,
          t,
          analysis.fieldName
        ),
        renderAnalysisExtracted(
          analysis.fieldExtracted ?? ExtractTypeEnum.NAO_PREENCHIDO,
          t,
          analysis.fieldName
        ),
        renderAnalysisStatus(analysis.status, t),
      ];
    }) || [];

  const tableHeadersFormatted = tableHeaders.map((header) =>
    t(`modal-table-header-${header}`)
  );

  return (
    <div>
      <Dialog
        classes={{
          root: classes.center,
          paper: classes.modal,
        }}
        open={modal}
        keepMounted
        onClose={handleClose}
        aria-labelledby="modal-slide-title"
        aria-describedby="modal-slide-description"
      >
        <DialogTitle
          id="classic-modal-slide-title"
          disableTypography
          className={classes.modalHeader}
        >
          <h4 className={classes.modalTitle}>{t("modal-title")}</h4>
        </DialogTitle>
        <DialogContent
          id="modal-slide-description"
          className={classes.modalBody}
        >
          {isLoading ? (
            <Box width="500px">
              <AnalysisModalSkeleton />
            </Box>
          ) : analysis?.length === 1 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              marginTop="16px"
              width="400px"
            >
              <StyledParagraph>
                {renderAnalysisExtracted(analysis[0]?.fieldExtracted, t, "")}
              </StyledParagraph>
            </Box>
          ) : (
            <Table
              hover
              tableHeaderColor="success"
              tableHead={tableHeadersFormatted}
              tableData={analyzedContent}
            />
          )}
        </DialogContent>
        <DialogActions
          className={classes.modalFooter + " " + classes.modalFooterCenter}
        >
          <ButtonTS onClick={handleClose}>{t("modal-action-close")}</ButtonTS>
        </DialogActions>
      </Dialog>
    </div>
  );
}
